import React, { useState } from "react";
import { Eye, Sparkle, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ModelSelector } from "../components/ModelSelector";

const NICHES = ["gaming", "finance", "motivation", "ai", "fitness", "tech", "storytelling"];

export default function Competitor() {
  const { setCredits } = useAuth();
  const [handle, setHandle] = useState("");
  const [niche, setNiche] = useState("ai");
  const [titles, setTitles] = useState("");
  const [model, setModel] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!handle.trim()) { toast.error("Enter a handle"); return; }
    setLoading(true); setResult(null);
    try {
      const sample = titles.split("\n").map(t => t.trim()).filter(Boolean).slice(0, 10);
      const { data } = await api.post("/ai/competitor", { handle, niche, sample_titles: sample, model, compare });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setResult(data);
      toast.success(data.compare ? "Compared across models" : "Analysis ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="competitor-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// COMPETITOR ANALYZER</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Steal what works.</h1>
      <p className="text-white/50 mt-1">AI-powered competitor breakdown — patterns, gaps, content angles to adapt.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Handle / channel</label>
            <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@example_creator" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="competitor-handle-input" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Niche</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {NICHES.map(n => (
                <button key={n} onClick={() => setNiche(n)} className={`px-3 py-1.5 rounded-full text-xs capitalize ${niche === n ? "bg-[#FF3B6B] text-white" : "bg-white/5 border border-white/10 text-white/70"}`} data-testid={`competitor-niche-${n}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Recent titles (one per line, optional)</label>
            <textarea value={titles} onChange={(e) => setTitles(e.target.value)} rows={5} placeholder="Paste 5-10 of their recent video titles" className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="competitor-titles-input" />
          </div>
          <ModelSelector type="text" action="competitor" model={model} compare={compare} onChange={({ model: m, compare: c }) => { setModel(m); setCompare(c); }} />
          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="analyze-competitor-btn">
            {loading ? (compare ? "Comparing 3 models..." : "Analyzing...") : <><Sparkle weight="fill" size={16} /> {compare ? "Compare across models" : "Analyze"}</>}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-3">
          {!result && !loading && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed h-full flex flex-col items-center justify-center">
              <Eye size={50} className="mb-3 text-white/20" />
              <p>Competitor breakdown will appear here</p>
            </div>
          )}
          {result && !result.compare && <CompetitorCards data={result} handle={result.handle} niche={result.niche} />}
          {result?.compare && (
            <div className="space-y-6">
              {result.variants.map((v) => (
                <div key={v.model} className="space-y-3" data-testid={`variant-${v.model}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">{v.label}</span>
                    {v.error && <span className="text-xs text-red-400">failed: {v.error}</span>}
                  </div>
                  {v.data && <CompetitorCards data={v.data} handle={result.handle} niche={result.niche} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const CompetitorCards = ({ data, handle, niche }) => (
  <>
    <div className="glass rounded-xl p-6" data-testid="competitor-score">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-white/50 uppercase tracking-wider">Overall strength</p>
          <p className="font-medium mt-1 capitalize">{handle} · {niche}</p>
        </div>
        <div className="font-mono">
          <span className="text-[#FF3B6B] text-4xl font-bold">{data.overall_score}</span>
          <span className="text-white/50">/100</span>
        </div>
      </div>
    </div>
    {[
      { key: "hook_patterns", label: "Hook patterns they repeat", color: "#FF3B6B" },
      { key: "strengths", label: "Strengths", color: "#00E5FF" },
      { key: "weaknesses", label: "Weaknesses (your opportunity)", color: "#EAB308" },
      { key: "steal_ideas", label: "Content angles to adapt", color: "#FF3B6B" },
    ].map(s => (
      <div key={s.key} className="glass rounded-xl p-6" data-testid={`competitor-${s.key}`}>
        <p className="text-xs font-mono uppercase tracking-wider mb-3" style={{ color: s.color }}>{s.label}</p>
        <ul className="space-y-2">
          {(data[s.key] || []).map((it, i) => (
            <li key={i} className="text-sm text-white/80 flex items-start gap-2">
              <ArrowUpRight size={14} weight="bold" style={{ color: s.color }} className="mt-1 shrink-0" /> {it}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </>
);
